<template>
  <main-template>
    <v-row>
      <v-col>
        <h1>Busca Ativa — Visão Geral</h1>
      </v-col>
    </v-row>

    <v-row dense>
      <v-col v-for="card of cards" :key="card.titulo" cols="auto" class="card-indicador-col">
        <e-card-indicador
          :titulo="card.titulo"
          :subtitulo="card.subtitulo"
          :valor="card.valor"
          :icone="card.icone"
          :cor="card.cor"
          :carregando="carregandoIndicadores"
        />
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12" lg="5" class="d-flex flex-column">
        <v-progress-linear v-if="carregandoIndicadores" indeterminate height="3" />
        <e-grafico-por-categoria :dados="graficoPorCategoria" class="flex-grow-1" />

        <v-alert v-if="erroIndicadores" type="error" text dense class="mt-4 mb-0">
          Não foi possível carregar os indicadores.
          <v-btn small text color="error" @click="carregarIndicadores">Tentar novamente</v-btn>
        </v-alert>

        <v-alert
          v-if="alunosSemAcompanhamento > 0"
          type="warning"
          text
          border="left"
          class="mt-4 mb-0"
        >
          <div class="font-weight-bold">
            {{ alunosSemAcompanhamento }}
            {{ alunosSemAcompanhamento === 1 ? "aluno está" : "alunos estão" }}
            sem nenhum acompanhamento registrado.
          </div>
          <div class="text-body-2">Confira ao lado quais são através da Pesquisa por Filtros.</div>
        </v-alert>
      </v-col>

      <v-col cols="12" lg="7">
        <v-card outlined class="h-100 card-filtros">
          <v-card-title class="text-subtitle-1 font-weight-bold">
            Pesquisa por Filtros
            <v-spacer />
            <v-btn text small color="primary" @click="filtrosAbertos = !filtrosAbertos">
              {{ filtrosAbertos ? "Recolher" : "Editar filtros" }}
              <v-icon right small>{{
                filtrosAbertos ? "mdi-chevron-up" : "mdi-chevron-down"
              }}</v-icon>
            </v-btn>
          </v-card-title>
          <v-card-text v-show="!filtrosAbertos" class="pt-0 text-body-2">
            <template v-if="resumoDosFiltros.length">{{ resumoDosFiltros.join(" · ") }}</template>
            <template v-else>Nenhum filtro aplicado: todos os alunos em risco.</template>
          </v-card-text>
          <v-card-text v-show="filtrosAbertos" class="conteudo-filtros">
            <v-row dense>
              <v-col cols="12" sm="6" md="3" lg="6" xl="3">
                <e-label class="rotulo-filtro">Escola</e-label>
                <e-autocomplete
                  :items="opcoes.escolas"
                  :item-text="(escola) => escola.descricao"
                  :item-value="(escola) => escola.id"
                  v-model="filtros.escola_id"
                  @change="aoMudarEscola"
                  :loading="carregandoOpcoes"
                  aria-label="Escola"
                  placeholder="Todas as escolas"
                  clearable
                  dense
                  solo
                  hide-details
                />
                <div class="dica-filtro">&nbsp;</div>
              </v-col>
              <v-col cols="12" sm="6" md="3" lg="6" xl="3">
                <e-label class="rotulo-filtro">Turma</e-label>
                <e-autocomplete
                  :items="turmasDisponiveis"
                  :item-text="(turma) => turma.descricao"
                  :item-value="(turma) => turma.id"
                  v-model="filtros.turma_id"
                  @change="aoMudarTurma"
                  :disabled="!filtros.escola_id"
                  aria-label="Turma"
                  placeholder="Todas as turmas"
                  clearable
                  dense
                  solo
                  hide-details
                />
                <div class="dica-filtro">
                  {{ !filtros.escola_id ? "Selecione uma escola primeiro." : "\u00a0" }}
                </div>
              </v-col>
              <v-col cols="12" sm="6" md="3" lg="6" xl="3">
                <e-label class="rotulo-filtro">Série</e-label>
                <e-autocomplete
                  :items="opcoes.series"
                  :item-text="(serie) => serie.descricao"
                  :item-value="(serie) => serie.id"
                  v-model="filtros.serie_id"
                  @change="aoMudarSerie"
                  :disabled="!filtros.turma_id"
                  aria-label="Série"
                  placeholder="Todas as séries"
                  clearable
                  dense
                  solo
                  hide-details
                />
                <div class="dica-filtro">{{ dicaSerie }}</div>
              </v-col>
              <v-col cols="12" sm="6" md="3" lg="6" xl="3">
                <e-label class="rotulo-filtro">Turno</e-label>
                <e-autocomplete
                  :items="opcoes.turnos"
                  :item-text="(turno) => turno.descricao"
                  :item-value="(turno) => turno.id"
                  v-model="filtros.turno_id"
                  :disabled="!filtros.serie_id"
                  aria-label="Turno"
                  placeholder="Todos os turnos"
                  clearable
                  dense
                  solo
                  hide-details
                />
                <div class="dica-filtro">{{ dicaTurno }}</div>
              </v-col>

              <v-col cols="12" sm="6" md="3" lg="6" xl="3">
                <e-label class="rotulo-filtro">Risco de Evasão</e-label>
                <e-autocomplete
                  :items="opcoes.categorias"
                  :item-text="(categoria) => categoria.rotulo"
                  :item-value="(categoria) => categoria.valor"
                  v-model="filtros.categoria"
                  aria-label="Risco de Evasão"
                  placeholder="Todos os riscos"
                  clearable
                  dense
                  solo
                  hide-details
                />
              </v-col>
              <v-col cols="12" sm="6" md="3" lg="6" xl="3">
                <e-label class="rotulo-filtro">Mín. de dias consecutivos</e-label>
                <e-autocomplete
                  :items="opcoes.minimos_dias_consecutivos"
                  :item-text="(minimo) => `${minimo} ou mais`"
                  :item-value="(minimo) => minimo"
                  v-model="filtros.minimo_dias_consecutivos"
                  aria-label="Quantidade mínima de dias consecutivos"
                  placeholder="Sem mínimo de dias"
                  clearable
                  dense
                  solo
                  hide-details
                />
              </v-col>
              <v-col cols="12" md="6" lg="12" xl="6">
                <e-label class="rotulo-filtro">Nome do aluno</e-label>
                <v-text-field
                  v-model="filtros.nome"
                  aria-label="Nome do aluno"
                  placeholder="Digite o nome do aluno"
                  append-icon="mdi-magnify"
                  @keyup.enter="pesquisar()"
                  clearable
                  dense
                  solo
                  hide-details
                />
              </v-col>
            </v-row>

            <v-row dense class="mt-0">
              <v-col cols="12" class="d-flex flex-wrap" style="gap: 0 24px">
                <v-checkbox
                  v-model="filtros.casos_encerrados"
                  label="Casos encerrados"
                  dense
                  hide-details
                  class="mt-0"
                />
                <v-checkbox
                  v-model="filtros.sem_acompanhamento"
                  label="Sem acompanhamento"
                  dense
                  hide-details
                  class="mt-0"
                />
              </v-col>
            </v-row>

            <v-row dense align="center" class="mt-2 acoes-filtros">
              <v-col
                cols="12"
                md="auto"
                lg="12"
                xl="auto"
                class="d-flex align-center"
                style="gap: 8px"
              >
                <v-btn color="primary" depressed :loading="carregandoCasos" @click="pesquisar()">
                  <v-icon left>mdi-magnify</v-icon>
                  Pesquisar
                </v-btn>
                <v-btn small text color="primary" @click="limparFiltros">Limpar filtros</v-btn>
              </v-col>
              <v-col cols="12" md lg="12" xl>
                <v-radio-group
                  v-model="filtros.ordenar_por"
                  row
                  dense
                  hide-details
                  class="mt-0 justify-md-end"
                >
                  <e-label class="mr-4">Ordenar por:</e-label>
                  <v-radio label="Ordem Alfabética" value="alfabetica" />
                  <v-radio label="Risco de Evasão" value="risco" />
                </v-radio-group>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12">
        <v-card outlined>
          <v-card-title class="text-subtitle-1 font-weight-bold">
            <span ref="tituloResultados" tabindex="-1">
              Resultados da Pesquisa ({{ paginacao.total }})
            </span>
            <v-spacer />
            <v-btn color="primary" outlined small @click="modalRelatorio = true">
              <v-icon left small>mdi-file-pdf-box</v-icon>
              Gerar relatório
            </v-btn>
          </v-card-title>

          <div
            v-if="resumoDosFiltros.length"
            class="px-4 pb-2 d-flex flex-wrap align-center"
            style="gap: 4px"
          >
            <span class="text-caption grey--text text--darken-1 mr-1">Filtros aplicados:</span>
            <v-chip v-for="item of resumoDosFiltros" :key="item" x-small label outlined>
              {{ item }}
            </v-chip>
          </div>

          <e-tabela-alunos-em-risco
            :casos="casos"
            :carregando="carregandoCasos"
            :erro="erroCasos"
            mostrar-turma
            mensagem-vazia="Nenhum aluno encontrado com os filtros selecionados. Ajuste ou limpe os filtros e pesquise novamente."
            @tentar-novamente="pesquisar(paginacao.current_page)"
          />

          <div
            v-if="paginacao.total > 0"
            class="d-flex flex-wrap align-center justify-space-between px-4 py-3"
            style="gap: 8px 24px"
          >
            <span class="text-body-2" role="status" aria-live="polite">
              Exibindo {{ paginacao.from }}–{{ paginacao.to }} de {{ paginacao.total }} resultados
            </span>
            <e-paginate
              ref="paginacao"
              v-if="paginacao.last_page > 1"
              :key="`paginacao-${chaveDaPesquisa}`"
              :current_page="paginacao.current_page"
              :last_page="paginacao.last_page"
              :pageNumber="paginacao.current_page"
              :search="filtros.nome || ''"
              @changeActionResponse="pesquisarPagina"
            />
            <div class="d-flex align-center" style="gap: 8px">
              <span class="text-body-2">Por página</span>
              <v-select
                v-model="porPagina"
                :items="opcoesPorPagina"
                aria-label="Quantidade de resultados por página"
                dense
                solo
                flat
                outlined
                hide-details
                style="max-width: 90px"
                @change="pesquisar()"
              />
            </div>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <e-modal-relatorio-busca-ativa
      v-model="modalRelatorio"
      :escolas="opcoes.escolas"
      :turmas="opcoes.turmas"
      :escola-inicial="filtros.escola_id"
      :turma-inicial="filtros.turma_id"
    />
  </main-template>
</template>

<script>
import ECardIndicador from "@/components/BuscaAtiva/ECardIndicador.vue";
import EGraficoPorCategoria from "@/components/BuscaAtiva/EGraficoPorCategoria.vue";
import EModalRelatorioBuscaAtiva from "@/components/BuscaAtiva/EModalRelatorioBuscaAtiva.vue";
import ETabelaAlunosEmRisco from "@/components/BuscaAtiva/ETabelaAlunosEmRisco.vue";

const CHAVE_ESTADO = "buscaAtiva.visaoGeral.pesquisa";

const FILTROS_VAZIOS = {
  escola_id: null,
  turma_id: null,
  serie_id: null,
  turno_id: null,
  categoria: null,
  minimo_dias_consecutivos: null,
  nome: null,
  casos_encerrados: false,
  sem_acompanhamento: false,
  ordenar_por: "risco",
};

export default {
  components: {
    ECardIndicador,
    EGraficoPorCategoria,
    EModalRelatorioBuscaAtiva,
    ETabelaAlunosEmRisco,
  },
  data() {
    return {
      indicadores: {},
      graficoPorCategoria: [],
      alunosSemAcompanhamento: 0,
      opcoes: {
        escolas: [],
        turmas: [],
        series: [],
        turnos: [],
        categorias: [],
        minimos_dias_consecutivos: [],
      },
      filtros: { ...FILTROS_VAZIOS },
      casos: [],
      paginacao: { current_page: 1, last_page: 1, per_page: 15, total: 0 },
      filtrosAbertos: true,
      filtrosAplicados: { ...FILTROS_VAZIOS },
      porPagina: 15,
      opcoesPorPagina: [15, 30, 50, 100],
      carregandoCasos: false,
      carregandoIndicadores: false,
      carregandoOpcoes: false,
      erroIndicadores: false,
      erroCasos: null,
      chaveDaPesquisa: 0,
      modalRelatorio: false,
    };
  },
  computed: {
    cards() {
      const cards = this.indicadores;
      return [
        {
          titulo: "Total de Alunos em Busca Ativa",
          valor: cards.total_em_busca_ativa ?? 0,
          icone: "mdi-account-group",
          cor: "indigo",
        },
        {
          titulo: "Novos Casos",
          subtitulo: "(mês atual)",
          valor: cards.novos_casos_no_mes ?? 0,
          icone: "mdi-account-plus",
          cor: "teal",
        },
        {
          titulo: "Casos em Acompanhamento",
          valor: cards.casos_em_acompanhamento ?? 0,
          icone: "mdi-target-account",
          cor: "blue",
        },
        {
          titulo: "Casos Encerrados",
          valor: cards.casos_encerrados ?? 0,
          icone: "mdi-check-circle-outline",
          cor: "amber",
        },
        {
          titulo: "Casos Críticos",
          valor: cards.casos_criticos ?? 0,
          icone: "mdi-alert",
          cor: "red",
        },
      ];
    },
    turmasDisponiveis() {
      if (!this.filtros.escola_id) return [];
      return this.opcoes.turmas.filter((turma) => turma.configuracao_id === this.filtros.escola_id);
    },
    dicaSerie() {
      if (!this.filtros.escola_id) return "Selecione uma escola e uma turma primeiro.";
      if (!this.filtros.turma_id) return "Selecione uma turma primeiro.";
      return "\u00a0";
    },
    dicaTurno() {
      if (!this.filtros.escola_id) return "Selecione escola, turma e série primeiro.";
      if (!this.filtros.turma_id) return "Selecione turma e série primeiro.";
      if (!this.filtros.serie_id) return "Selecione uma série primeiro.";
      return "\u00a0";
    },
    resumoDosFiltros() {
      const f = this.filtrosAplicados;
      const descricao = (lista, id, chave = "id", texto = "descricao") =>
        lista.find((item) => item[chave] === id)?.[texto];
      return [
        f.escola_id && `Escola: ${descricao(this.opcoes.escolas, f.escola_id)}`,
        f.turma_id && `Turma: ${descricao(this.opcoes.turmas, f.turma_id)}`,
        f.serie_id && `Série: ${descricao(this.opcoes.series, f.serie_id)}`,
        f.turno_id && `Turno: ${descricao(this.opcoes.turnos, f.turno_id)}`,
        f.categoria &&
          `Risco: ${descricao(this.opcoes.categorias, f.categoria, "valor", "rotulo")}`,
        f.minimo_dias_consecutivos && `${f.minimo_dias_consecutivos}+ dias consecutivos`,
        f.nome && `Nome: ${f.nome}`,
        f.casos_encerrados && "Casos encerrados",
        f.sem_acompanhamento && "Sem acompanhamento",
      ].filter(Boolean);
    },
  },
  async mounted() {
    this.carregarIndicadores();
    await this.carregarOpcoes();
    const estado = this.lerEstadoSalvo();
    if (estado) {
      // Restaura apenas os valores dos campos e reaplica a pesquisa na página em que o usuário estava.
      this.filtros = { ...FILTROS_VAZIOS, ...estado.filtros };
      this.filtrosAplicados = { ...this.filtros };
      this.porPagina = estado.porPagina ?? this.porPagina;
      await this.pesquisar(estado.pagina, { manterFiltrosAplicados: true });
      return;
    }
    this.pesquisar();
  },
  methods: {
    async carregarIndicadores() {
      this.carregandoIndicadores = true;
      this.erroIndicadores = false;
      try {
        const dados = await this.$services.buscaAtivaService.visaoGeral();
        this.indicadores = dados.cards;
        this.graficoPorCategoria = dados.grafico_por_categoria;
        this.alunosSemAcompanhamento = dados.alunos_sem_acompanhamento;
      } catch (error) {
        this.erroIndicadores = true;
        this.$handleError(error);
      }
      this.carregandoIndicadores = false;
    },
    async carregarOpcoes() {
      this.carregandoOpcoes = true;
      try {
        this.opcoes = await this.$services.buscaAtivaService.opcoesDeFiltro();
      } catch (error) {
        this.$handleError(error);
      }
      this.carregandoOpcoes = false;
    },
    // Nunca repassar o evento de clique/teclado como número de página.
    async pesquisar(pagina = 1, { manterFiltrosAplicados = false } = {}) {
      const numeroDaPagina = Number.isInteger(pagina) && pagina > 0 ? pagina : 1;

      if (numeroDaPagina === 1 && !manterFiltrosAplicados) {
        this.chaveDaPesquisa += 1;
        this.filtrosAplicados = { ...this.filtros };
      }

      this.carregandoCasos = true;
      this.erroCasos = null;
      // Ao trocar os filtros, não mantém a lista antiga na tela enquanto a nova carrega.
      if (numeroDaPagina === 1 && !manterFiltrosAplicados) this.casos = [];

      try {
        const resposta = await this.$services.buscaAtivaService.casos({
          ...this.filtrosAplicados,
          por_pagina: this.porPagina,
          page: numeroDaPagina,
        });
        this.casos = resposta.data;
        this.paginacao = resposta.meta ?? this.paginacao;
        this.salvarEstado();
      } catch (error) {
        this.casos = [];
        this.erroCasos =
          "Não foi possível carregar os alunos. Verifique sua conexão e tente novamente.";
        this.$handleError(error);
      }
      this.carregandoCasos = false;
    },
    async pesquisarPagina(pagina) {
      await this.pesquisar(pagina);
      this.$nextTick(() => {
        // Foco no botão da página selecionada; se não existir, no título dos resultados.
        const botao = this.$refs.paginacao?.$el?.querySelector(".v-pagination__item--active");
        const titulo = this.$refs.tituloResultados;
        const alvo = botao ?? titulo;
        if (alvo) alvo.focus({ preventScroll: true });
        if (titulo) titulo.scrollIntoView({ block: "start", behavior: "smooth" });
      });
    },
    lerEstadoSalvo() {
      try {
        return JSON.parse(sessionStorage.getItem(CHAVE_ESTADO));
      } catch (error) {
        return null;
      }
    },
    salvarEstado() {
      try {
        sessionStorage.setItem(
          CHAVE_ESTADO,
          JSON.stringify({
            filtros: this.filtrosAplicados,
            porPagina: this.porPagina,
            pagina: this.paginacao.current_page,
          })
        );
      } catch (error) {
        // Sem armazenamento disponível: a página apenas não é restaurada.
      }
    },
    aoMudarEscola() {
      this.filtros.turma_id = null;
      this.filtros.serie_id = null;
      this.filtros.turno_id = null;
    },
    aoMudarTurma() {
      this.filtros.serie_id = null;
      this.filtros.turno_id = null;
    },
    aoMudarSerie() {
      this.filtros.turno_id = null;
    },
    async limparFiltros() {
      this.filtros = { ...FILTROS_VAZIOS };
      await this.pesquisar();
    },
  },
};
</script>

<style scoped>
/* Card com a altura da coluna: os filtros ficam no topo e as ações encostam no rodapé. */
.card-filtros {
  display: flex;
  flex-direction: column;
}

.conteudo-filtros {
  flex: 1 1 auto;
  flex-direction: column;
}

.conteudo-filtros:not([style*="display: none"]) {
  display: flex;
}

.acoes-filtros {
  margin-top: auto !important;
}

.h-100 {
  height: 100%;
}

/* Cada card cresce para preencher a linha e quebra quando falta espaço (mín. 220px). */
.card-indicador-col {
  flex: 1 1 220px;
  min-width: 0;
}

.rotulo-filtro {
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.dica-filtro {
  min-height: 18px;
  font-size: 12px;
  line-height: 18px;
  color: rgba(0, 0, 0, 0.6);
}
</style>
